import React from "react";
import { AUTH_PREFIX_PATH, APP_PREFIX_PATH } from "configs/AppConfig";
import { RESOURCES } from "./NavigationConfig";

export const publicRoutes = [
  {
    key: "login",
    path: `/`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login")
    ),
  },
  {
    key: "login-alt",
    path: `/login`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login")
    ),
  },
  {
    key: "login-1",
    path: `${AUTH_PREFIX_PATH}/login-1`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login-1")
    ),
  },
  {
    key: "login-2",
    path: `${AUTH_PREFIX_PATH}/login-2`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login-2")
    ),
  },
  {
    key: "register-1",
    path: `${AUTH_PREFIX_PATH}/register-1`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/register-1")
    ),
  },
  {
    key: "register-2",
    path: `${AUTH_PREFIX_PATH}/register-2`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/register-2")
    ),
  },
  {
    key: "forgot-password",
    path: `${AUTH_PREFIX_PATH}/forgot-password`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/forgot-password")
    ),
  },
  {
    key: "error-page-1",
    path: `${AUTH_PREFIX_PATH}/error-page-1`,
    component: React.lazy(() => import("views/auth-views/errors/error-page-1")),
  },
  {
    key: "error-page-2",
    path: `${AUTH_PREFIX_PATH}/error-page-2`,
    component: React.lazy(() => import("views/auth-views/errors/error-page-2")),
  },
  // Patient Portal (public - token-based auth)
  {
    key: "patient-portal",
    path: `/portal/forms/:token`,
    component: React.lazy(() => import("views/portal/PatientFormPortal")),
  },
  // Check-in Portal (public - for QR code scanning)
  {
    key: "checkin-portal",
    path: `/portal/checkin/:checkinId`,
    component: React.lazy(() => import("views/portal/CheckinPortal")),
  },
  // Payment Portal (public - token-based)
  {
    key: "payment-portal",
    path: `/portal/payment/:token`,
    component: React.lazy(() => import("views/portal/PaymentPortal")),
  },
];

export const protectedRoutes = [
  // Set Password (for temporary password flow)
  {
    key: "set-password",
    path: `${AUTH_PREFIX_PATH}/set-password`,
    component: React.lazy(() =>
      import("views/auth-views/components/SetPasswordForm")
    ),
    meta: {
      blankLayout: true
    }
  },
  // Profile and Account routes
  {
    key: "profile",
    path: `${APP_PREFIX_PATH}/profile`,
    component: React.lazy(() => import("views/app-views/profile")),
  },
  {
    key: "change-password",
    path: `${APP_PREFIX_PATH}/change-password`,
    component: React.lazy(() => import("views/app-views/change-password")),
  },
  
  // ==================== DASHBOARD ====================
  {
    key: "app-dashboard",
    path: `${APP_PREFIX_PATH}/dashboard`,
    component: React.lazy(() => import("views/app-views/clinic/dashboard")),
    permission: { resource: RESOURCES.DASHBOARD, action: 'read' },
  },
  // iPad Kiosk Check-In (no permission check - dedicated device, login is enough)
  {
    key: "app-kiosk",
    path: `${APP_PREFIX_PATH}/kiosk`,
    component: React.lazy(() => import("views/app-views/clinic/kiosk")),
    // No permission required - just being logged in is sufficient for kiosk
  },
  // Quick Check-in (admin only - for reception to check-in patients)
  {
    key: "app-quick-checkin",
    path: `${APP_PREFIX_PATH}/quick-checkin`,
    component: React.lazy(() => import("views/app-views/clinic/quick-checkin")),
    permission: { resource: RESOURCES.CLINIC_PATIENTS, action: 'create' },
    adminOnly: true,
  },
  // Check-ins History (admin only - view all check-ins with filters)
  {
    key: "app-checkins",
    path: `${APP_PREFIX_PATH}/checkins`,
    component: React.lazy(() => import("views/app-views/clinic/checkins")),
    permission: { resource: RESOURCES.CLINIC_PATIENTS, action: 'read' },
    adminOnly: true,
  },
  // Practitioner Dashboard (for staff/practitioners to see their patients & check-ins)
  {
    key: "app-practitioner-dashboard",
    path: `${APP_PREFIX_PATH}/my-dashboard`,
    component: React.lazy(() => import("views/app-views/clinic/practitioner-dashboard")),
    // No specific permission - just needs to be logged in staff
  },
  
  // ==================== CLINIC ====================
  // Patients
  {
    key: "app-patients",
    path: `${APP_PREFIX_PATH}/clinic/patients`,
    component: React.lazy(() => import("views/app-views/clinic/patients")),
    permission: { resource: RESOURCES.CLINIC_PATIENTS, action: 'read' },
  },
  {
    key: "app-patient-detail",
    path: `${APP_PREFIX_PATH}/clinic/patients/:id`,
    component: React.lazy(() => import("views/app-views/clinic/patients/PatientDetail")),
    permission: { resource: RESOURCES.CLINIC_PATIENTS, action: 'read' },
  },
  // Treatment Cases
  {
    key: "app-treatment-cases",
    path: `${APP_PREFIX_PATH}/clinic/treatment-cases`,
    component: React.lazy(() => import("views/app-views/clinic/treatment-cases")),
    permission: { resource: RESOURCES.CLINIC_PATIENTS, action: 'read' },
  },
  {
    key: "app-treatment-case-detail",
    path: `${APP_PREFIX_PATH}/clinic/treatment-cases/:id`,
    component: React.lazy(() => import("views/app-views/clinic/treatment-cases/detail")),
    permission: { resource: RESOURCES.CLINIC_PATIENTS, action: 'read' },
  },
  // SOAP Notes
  {
    key: "app-soap-notes",
    path: `${APP_PREFIX_PATH}/clinic/soap-notes`,
    component: React.lazy(() => import("views/app-views/clinic/soap-notes")),
    permission: { resource: RESOURCES.CLINIC_SOAP_NOTES, action: 'read' },
  },
  {
    key: "app-soap-note-editor",
    path: `${APP_PREFIX_PATH}/clinic/soap-notes/:id`,
    component: React.lazy(() => import("views/app-views/clinic/soap-notes/SOAPNoteEditor")),
    // Allow read access - component will check create/update permissions internally
    permission: { resource: RESOURCES.CLINIC_SOAP_NOTES, action: 'read' },
  },

  // ==================== ASSESSMENTS ====================
  {
    key: "app-assessments",
    path: `${APP_PREFIX_PATH}/clinic/assessments`,
    component: React.lazy(() => import("views/app-views/clinic/initial-assessment/list")),
    permission: { resource: RESOURCES.CLINIC_ASSESSMENTS, action: 'read' },
  },
  {
    key: "app-assessment-new",
    path: `${APP_PREFIX_PATH}/clinic/initial-assessment/new`,
    component: React.lazy(() => import("views/app-views/clinic/initial-assessment")),
    permission: { resource: RESOURCES.CLINIC_ASSESSMENTS, action: 'create' },
  },
  {
    key: "app-assessment-detail",
    path: `${APP_PREFIX_PATH}/clinic/initial-assessment/:id`,
    component: React.lazy(() => import("views/app-views/clinic/initial-assessment")),
    permission: { resource: RESOURCES.CLINIC_ASSESSMENTS, action: 'read' },
  },

  // ==================== FORMS ====================
  {
    key: "app-form-assignments",
    path: `${APP_PREFIX_PATH}/forms/assignments`,
    component: React.lazy(() => import("views/app-views/clinic/forms/assignments")),
    permission: { resource: RESOURCES.CLINIC_FORM_ASSIGNMENTS, action: 'read' },
  },
  {
    key: "app-form-templates",
    path: `${APP_PREFIX_PATH}/forms/templates`,
    component: React.lazy(() => import("views/app-views/clinic/forms/templates")),
    permission: { resource: RESOURCES.CLINIC_FORM_TEMPLATES, action: 'read' },
  },
  {
    key: "app-form-template-builder",
    path: `${APP_PREFIX_PATH}/forms/templates/:id`,
    component: React.lazy(() => import("views/app-views/clinic/forms/templates/FormTemplateBuilder")),
    // Allow read access - component will check create/update permissions internally
    permission: { resource: RESOURCES.CLINIC_FORM_TEMPLATES, action: 'read' },
  },
  {
    key: "app-form-packages",
    path: `${APP_PREFIX_PATH}/forms/packages`,
    component: React.lazy(() => import("views/app-views/clinic/forms/packages")),
    permission: { resource: RESOURCES.CLINIC_FORM_PACKAGES, action: 'read' },
  },

  // ==================== SETUP ====================
  // Services
  {
    key: "app-services",
    path: `${APP_PREFIX_PATH}/setup/services`,
    component: React.lazy(() => import("views/app-views/clinic/services")),
    permission: { resource: RESOURCES.CLINIC_SERVICES, action: 'read' },
    adminOnly: true,
  },
  // Staff Profiles
  {
    key: "app-staff",
    path: `${APP_PREFIX_PATH}/setup/staff`,
    component: React.lazy(() => import("views/app-views/clinic/setup/staff")),
    permission: { resource: RESOURCES.CLINIC_STAFF, action: 'read' },
    adminOnly: true, // Only admin can manage staff profiles
  },
  // SOAP Templates
  {
    key: "app-soap-templates",
    path: `${APP_PREFIX_PATH}/setup/soap-templates`,
    component: React.lazy(() => import("views/app-views/clinic/setup/soap-templates")),
    permission: { resource: RESOURCES.CLINIC_SOAP_TEMPLATES, action: 'read' },
  },

  // ==================== PAYMENTS ====================
  {
    key: "app-payments",
    path: `${APP_PREFIX_PATH}/payments`,
    component: React.lazy(() => import("views/app-views/clinic/payments")),
    permission: { resource: RESOURCES.CLINIC_BILLING, action: 'read' },
  },

  // ==================== REPORTS ====================
  {
    key: "app-reports",
    path: `${APP_PREFIX_PATH}/reports`,
    component: React.lazy(() => import("views/app-views/clinic/reports")),
    permission: { resource: RESOURCES.CLINIC_REPORTS, action: 'read' },
  },

  // ==================== ADMIN ====================
  // User Management
  {
    key: "app-users",
    path: `${APP_PREFIX_PATH}/admin/users`,
    component: React.lazy(() => import("views/app-views/sab/users")),
    permission: { resource: RESOURCES.USERS, action: 'read' },
  },
  // Backup & Restore
  {
    key: "app-backups",
    path: `${APP_PREFIX_PATH}/admin/backups`,
    component: React.lazy(() => import("views/app-views/sab/backups")),
    adminOnly: true,
  },
  // Audit Logs
  {
    key: "app-audit-logs",
    path: `${APP_PREFIX_PATH}/admin/audit-logs`,
    component: React.lazy(() => import("views/app-views/clinic/audit-logs")),
    permission: { resource: RESOURCES.CLINIC_AUDIT_LOGS, action: 'read' },
  },
];
